import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import { Crown, TrendingUp, CreditCard, Users } from "lucide-react";

const SUBTABS = [
    { id: "overview", label: "Overview", icon: Crown },
    { id: "recent", label: "Recent tier changes", icon: TrendingUp },
    { id: "payments", label: "Payments", icon: CreditCard },
];

export default function SubscriptionsTab() {
    const [sub, setSub] = useState("overview");

    return (
        <div className="space-y-4">
            <div className="flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1 w-fit flex-wrap">
                {SUBTABS.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`subs-subtab-${s.id}`}
                        onClick={() => setSub(s.id)}
                        className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                            sub === s.id ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                        }`}
                    >
                        <s.icon className="w-3.5 h-3.5" /> {s.label}
                    </button>
                ))}
            </div>
            {sub === "overview" && <Overview />}
            {sub === "recent" && <Recent />}
            {sub === "payments" && <Payments />}
        </div>
    );
}

function Overview() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/subscriptions/overview")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);
    if (!data) return <div className="text-slate-gray text-sm">Loading…</div>;
    const tierRows = Object.entries(data.tiers);
    return (
        <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-snowcap border border-ridge-line rounded-lg p-6" data-testid="subs-kpi-card">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-ember-orange" />
                    <h3 className="font-display font-bold text-lg text-midnight-pine">Subscribers</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <Kpi label="Total users" value={data.total_users} />
                    <Kpi label="Paying" value={data.paying_users} />
                    <Kpi label="% paying" value={`${data.paying_pct}%`} />
                </div>
            </div>
            <div className="bg-snowcap border border-ridge-line rounded-lg p-6">
                <h3 className="font-display font-bold text-lg text-midnight-pine mb-4">Tier breakdown</h3>
                <div className="space-y-2">
                    {tierRows.map(([tier, count]) => (
                        <div
                            key={tier}
                            data-testid={`subs-tier-row-${tier}`}
                            className="flex items-center justify-between py-2 border-b border-ridge-line last:border-0"
                        >
                            <span className="capitalize font-semibold text-granite">{tier}</span>
                            <span className="font-display font-black text-xl text-ember-orange tabular-nums">{count}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div>
            <div className="text-xs text-slate-gray font-bold uppercase tracking-widest mb-1">{label}</div>
            <div className="font-display font-black text-3xl text-midnight-pine tabular-nums">{value}</div>
        </div>
    );
}

function Recent() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/subscriptions/recent", { params: { limit: 100 } });
            setRows(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const onExport = () =>
        exportToCsv(
            rows,
            [
                { key: "admin_name", label: "Admin" },
                { key: "target_label", label: "User" },
                { key: "before", label: "Before", map: (r) => r.before?.tier || "" },
                { key: "after", label: "After", map: (r) => r.after?.tier || "" },
                { key: "reason", label: "Reason" },
                { key: "created_at", label: "When" },
            ],
            "subs-recent"
        );

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <button
                    data-testid="subs-recent-export"
                    onClick={onExport}
                    disabled={rows.length === 0}
                    className="px-3 py-1.5 rounded-md bg-midnight-pine text-white text-xs font-bold hover:bg-deep-evergreen disabled:opacity-40"
                >
                    Export CSV
                </button>
            </div>
            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-alpine-mist">
                        <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                            <th className="py-3 px-4">Admin</th>
                            <th className="py-3 px-4">User</th>
                            <th className="py-3 px-4">Before</th>
                            <th className="py-3 px-4">After</th>
                            <th className="py-3 px-4">Reason</th>
                            <th className="py-3 px-4">When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="py-10 text-center text-slate-gray">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={6} className="py-10 text-center text-slate-gray">No tier changes yet.</td></tr>
                        ) : rows.map((r, i) => (
                            <tr key={r.id || i} data-testid={`subs-recent-row-${i}`} className="border-t border-ridge-line">
                                <td className="py-3 px-4 text-granite">{r.admin_name || r.admin_id}</td>
                                <td className="py-3 px-4 text-xs text-slate-gray">{r.target_label || r.target_id}</td>
                                <td className="py-3 px-4 capitalize">{r.before?.tier || "—"}</td>
                                <td className="py-3 px-4 capitalize font-semibold text-ember-dark">{r.after?.tier || "—"}</td>
                                <td className="py-3 px-4 text-xs text-slate-gray max-w-[200px] truncate">{r.reason || "—"}</td>
                                <td className="py-3 px-4 text-xs text-slate-gray">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Payments() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/subscriptions/payments", { params: { limit: 100 } });
                setRows(data.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const onExport = () =>
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "user_id", label: "User ID" },
                { key: "tier", label: "Tier" },
                { key: "amount", label: "Amount" },
                { key: "currency", label: "Currency" },
                { key: "status", label: "Status" },
                { key: "provider", label: "Provider" },
                { key: "created_at", label: "When" },
            ],
            "subs-payments"
        );

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <button
                    data-testid="subs-payments-export"
                    onClick={onExport}
                    disabled={rows.length === 0}
                    className="px-3 py-1.5 rounded-md bg-midnight-pine text-white text-xs font-bold hover:bg-deep-evergreen disabled:opacity-40"
                >
                    Export CSV
                </button>
            </div>
            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-slate-gray text-sm">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-slate-gray text-sm">
                        No payment events recorded yet. Real-time payments will populate once Helcim is wired.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 px-4">User</th>
                                <th className="py-3 px-4">Tier</th>
                                <th className="py-3 px-4">Amount</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Provider</th>
                                <th className="py-3 px-4">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p, i) => (
                                <tr key={p.id || i} data-testid={`subs-payment-row-${i}`} className="border-t border-ridge-line">
                                    <td className="py-3 px-4 text-xs text-slate-gray font-mono">{p.user_id}</td>
                                    <td className="py-3 px-4 capitalize">{p.tier}</td>
                                    <td className="py-3 px-4 font-semibold">{p.currency || "USD"} {p.amount}</td>
                                    <td className="py-3 px-4 capitalize">{p.status}</td>
                                    <td className="py-3 px-4 text-xs text-slate-gray">{p.provider}</td>
                                    <td className="py-3 px-4 text-xs text-slate-gray">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
